/**
 * RedemptionPanels — shared UI for the mode-based access-code flows
 * (2026-08). Used by /pricing and /start so the two entry points stay
 * pixel-consistent and deterministic:
 *
 *   AccountQuestionPanel   — signed-out, valid unused code: ask (nicely)
 *       whether the user already has an account, and route sign-in vs
 *       create-account accordingly. Signing in first is required so the
 *       code applies to the right account (active → wallet credit,
 *       trial → extension, fresh → trial start).
 *
 *   RedemptionOutcomePanel — signed-in redemption confirmations for the
 *       non-onboarding modes: 'wallet_credit' (active account, credit
 *       added, no expiry mentioned) and 'trial_extension' (credit added +
 *       clock extended).
 *
 * Copy is lowercase by convention (matches wallet/portal style).
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import Spinner from '../common/Spinner';
// The panels' words are plain data in ./redemptionWords.ts (2026-09-30), read here and by the pricing page's rebuild in
// the new design, so the two cannot say different things. Re-exported for every existing caller.
import {
  ACCOUNT_QUESTION_WORDS,
  PROGRESS_WORDS,
  outcomeWords,
  formatCreditDollars,
  type RedemptionOutcome,
  type RedeemProgressStage,
} from './redemptionWords';

export { formatCreditDollars };
export type { RedemptionOutcome };

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #E6E4DE;
  border-radius: 1.25rem;
  padding: 1.75rem 1.5rem;
  box-sizing: border-box;
  width: 100%;
  animation: ${fadeIn} 0.35s ease both;
  text-align: center;

  @media (min-width: 640px) {
    padding: 2rem 2.25rem;
  }

  @media (min-width: 1024px) {
    padding: 2.5rem 3rem;
  }
`;

const PanelIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.6rem;
`;

const PanelTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  /* Avoid a lone orphaned word at tablet shell widths (progressive
     enhancement — older Safari ignores it harmlessly). */
  text-wrap: balance;
`;

const SpinnerRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.9rem;
`;

const PanelBody = styled.p`
  font-size: 0.9rem;
  color: #555;
  line-height: 1.65;
  max-width: 60ch;
  margin: 0 auto 1.25rem;

  @media (min-width: 1024px) {
    font-size: 0.95rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 420px) {
    flex-direction: column;
  }
`;

const PrimaryAction = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
`;

const SecondaryAction = styled.button`
  background: #fff;
  color: var(--brand-text);
  border: 1.5px solid #D8D5CE;
  border-radius: 0.85rem;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover { background: #F7F6F3; border-color: #B9B4A9; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Account question — signed-out, valid unused code
// ─────────────────────────────────────────────────────────────────────────────

export interface AccountQuestionPanelProps {
  onSignIn: () => void;
  onCreateAccount: () => void;
}

export const AccountQuestionPanel: React.FC<AccountQuestionPanelProps> = ({ onSignIn, onCreateAccount }) => (
  <Panel data-testid="redeem-account-question">
    <PanelIcon>{ACCOUNT_QUESTION_WORDS.icon}</PanelIcon>
    <PanelTitle>{ACCOUNT_QUESTION_WORDS.title}</PanelTitle>
    <PanelBody>{ACCOUNT_QUESTION_WORDS.body}</PanelBody>
    <ButtonRow>
      <PrimaryAction type="button" onClick={onSignIn}>
        {ACCOUNT_QUESTION_WORDS.signIn}
      </PrimaryAction>
      <SecondaryAction type="button" onClick={onCreateAccount}>
        {ACCOUNT_QUESTION_WORDS.createAccount}
      </SecondaryAction>
    </ButtonRow>
  </Panel>
);

// ─────────────────────────────────────────────────────────────────────────────
// Redemption outcome — wallet credit / trial extension confirmations
// ─────────────────────────────────────────────────────────────────────────────

export interface RedemptionOutcomePanelProps {
  outcome: RedemptionOutcome;
  onGoToWorkspace: () => void;
  onGoToWallet: () => void;
}

export const RedemptionOutcomePanel: React.FC<RedemptionOutcomePanelProps> = ({
  outcome,
  onGoToWorkspace,
  onGoToWallet,
}) => {
  const w = outcomeWords(outcome);
  const go = (to: 'wallet' | 'workspace') => (to === 'wallet' ? onGoToWallet : onGoToWorkspace);
  return (
    <Panel data-testid={outcome.mode === 'wallet_credit' ? 'redeem-outcome-wallet-credit' : 'redeem-outcome-trial-extension'}>
      <PanelIcon>{w.icon}</PanelIcon>
      <PanelTitle>{w.title}</PanelTitle>
      <PanelBody>{w.body}</PanelBody>
      <ButtonRow>
        <PrimaryAction type="button" onClick={go(w.primary.to)}>
          {w.primary.label}
        </PrimaryAction>
        {w.secondary && (
          <SecondaryAction type="button" onClick={go(w.secondary.to)}>
            {w.secondary.label}
          </SecondaryAction>
        )}
      </ButtonRow>
    </Panel>
  );
};

export interface RedeemProgressPanelProps {
  /**
   * Where the redemption is in its journey:
   *   'checking'  — code is being validated (pre-redeem)
   *   'applying'  — redeemAccessCode in flight (auth, RBAC bootstrap,
   *                 welcome seeding — can take several seconds)
   *   'applied'   — success beat before navigation to onboarding
   */
  stage: RedeemProgressStage;
}

/**
 * RedeemProgressPanel — the single in-shell progress card for code
 * redemption (2026-08-09 owner directive: after every submission it must be
 * abundantly clear the process is moving along). Extracted from the two
 * byte-similar inline panels in StartTrial.tsx / Pricing.tsx so the /start
 * and /pricing funnels cannot drift. Shows a live spinner while work is in
 * flight and a calm success beat once applied. Never renders a URL or code.
 */
export const RedeemProgressPanel: React.FC<RedeemProgressPanelProps> = ({ stage }) => (
  <Panel data-testid={`redeem-progress-${stage}`}>
    {stage === 'applied'
      ? <PanelIcon>✨</PanelIcon>
      : <SpinnerRow><Spinner /></SpinnerRow>}
    <PanelTitle>{PROGRESS_WORDS[stage].title}</PanelTitle>
    <PanelBody style={{ marginBottom: 0 }}>{PROGRESS_WORDS[stage].body}</PanelBody>
  </Panel>
);
